import { createSelector } from 'reselect'
import moment from 'moment'
import { LabFeatureFlags } from 'constants/lab'
import { isFeatureTogglePayload } from 'lib/lab/dynamicPayload'
import getCheckoutTotal from './payment/getCheckoutTotal'
import getInsuranceDestinationCountriesFromCart from './view/getInsuranceDestinationCountriesFromCart'
import { getOccupantsFromCartItems } from './request/insurance'
import getInsuranceDatesFromCart from './request/getInsuranceDatesFromCart'
import { unique } from 'lib/array/arrayUtils'

const DEFAULT_FEATURES: App.FunnelFeatureToggles = {
  walledGarden: true,
  roomUpgrade: true,
  luxPlus: true,
  flights: true,
  experiences: true,
  cfmr: true,
  travelProtection: true,
  carHire: true,
}

export const getFunnelFeatures = createSelector(
  (state: App.State) => state.system.features.labFlags[LabFeatureFlags.enableFunnelOptimiser],
  (funnelFlag: App.LabFeatureFlagResult): App.FunnelFeatureToggles => {
    if (!funnelFlag?.enabled) {
      return DEFAULT_FEATURES
    }

    const funnelToggles = funnelFlag.value

    if (!isFeatureTogglePayload(funnelToggles)) {
      return DEFAULT_FEATURES
    }

    return {
      walledGarden: funnelToggles.toggles?.walledGarden ?? DEFAULT_FEATURES.walledGarden,
      roomUpgrade: funnelToggles.toggles?.roomUpgrade ?? DEFAULT_FEATURES.roomUpgrade,
      luxPlus: funnelToggles.toggles?.luxPlus ?? DEFAULT_FEATURES.luxPlus,
      flights: funnelToggles.toggles?.flights ?? DEFAULT_FEATURES.flights,
      experiences: funnelToggles.toggles?.experiences ?? DEFAULT_FEATURES.experiences,
      cfmr: funnelToggles.toggles?.cfmr ?? DEFAULT_FEATURES.cfmr,
      travelProtection: funnelToggles.toggles?.travelProtection ?? DEFAULT_FEATURES.travelProtection,
      carHire: funnelToggles.toggles?.carHire ?? DEFAULT_FEATURES.carHire,
    }
  },
)

/**
 * Builds cart attributes for funnel optimiser evaluation.
 * These attributes are sent to svc-lab for audience targeting based on cart contents.
 */
export const buildFunnelCartAttributes = createSelector(
  (state: App.State) => getCheckoutTotal(state),
  (state: App.State) => getInsuranceDestinationCountriesFromCart(state),
  (state: App.State) => getOccupantsFromCartItems(state),
  (state: App.State) => getInsuranceDatesFromCart(state),
  (state: App.State) => state.checkout.cart.items,
  (totalPrice, destinations, occupancy, dates, items): Record<string, unknown> => {
    const today = moment()
    const startDate = dates?.startDate ? moment(dates.startDate) : null
    const endDate = dates?.endDate ? moment(dates.endDate) : null
    const leadTimeDays = startDate ? startDate.diff(today, 'days') : null
    const lengthOfStayNights = startDate && endDate ? endDate.diff(startDate, 'days') : null
    const productTypes = unique(items.map(item => item.itemType))
    const offerIds = unique(items.map(item => 'offerId' in item ? item.offerId : null).filter(Boolean))

    return {
      cartTotalPrice: totalPrice,
      destinationCountries: destinations,
      numberOfAdults: occupancy?.adults ?? 0,
      numberOfChildren: occupancy?.children ?? 0,
      numberOfInfants: occupancy?.infants ?? 0,
      numberOfGuests: (occupancy?.adults ?? 0) + (occupancy?.children ?? 0) + (occupancy?.infants ?? 0),
      leadTimeDays,
      lengthOfStayNights,
      productTypes,
      offerIds,
    }
  },
)
